import React, { useEffect, useRef, useState } from "react";
import { HelpCircle, X, Send, Bot, MessageCircleQuestion, Sparkles, Plus, Pencil, Trash2, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Bubble = ({ role, text }) => (
  <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`}>
    <div
      className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
        role === "user"
          ? "bg-primary text-primary-foreground rounded-br-sm"
          : "bg-secondary text-foreground rounded-bl-sm"
      }`}
    >
      {text}
    </div>
  </div>
);

export default function HelpAssistant() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("faq");
  const [faqs, setFaqs] = useState([]);
  const [expanded, setExpanded] = useState(null);

  // chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const endRef = useRef(null);

  // admin edit state
  const [editing, setEditing] = useState(null); // id | "new" | null
  const [form, setForm] = useState({ question: "", answer: "" });

  const loadFaq = () => api.get("/faq").then((r) => setFaqs(r.data || [])).catch(() => {});

  useEffect(() => { if (open) loadFaq(); }, [open]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setSending(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: text, session_id: sessionId });
      if (data.session_id) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Désolé, je n'ai pas pu répondre. Réessayez ou contactez le club au +32 499 75 51 76." }]);
    } finally {
      setSending(false);
    }
  };

  const askQuestion = (q) => {
    setTab("chat");
    setInput(q);
  };

  const saveFaq = async () => {
    if (!form.question.trim() || !form.answer.trim()) { toast.error("Question et réponse obligatoires"); return; }
    try {
      if (editing === "new") await api.post("/faq", form);
      else await api.put(`/faq/${editing}`, form);
      toast.success("Enregistré");
      setEditing(null); setForm({ question: "", answer: "" });
      loadFaq();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeFaq = async (id) => {
    if (!window.confirm("Supprimer cette question ?")) return;
    await api.delete(`/faq/${id}`);
    loadFaq();
  };

  const startEdit = (f) => { setEditing(f.id); setForm({ question: f.question, answer: f.answer }); };
  const startNew = () => { setEditing("new"); setForm({ question: "", answer: "" }); };

  return (
    <>
      <button
        data-testid="help-fab"
        onClick={() => setOpen((o) => !o)}
        aria-label="Aide et FAQ"
        className="fixed bottom-6 left-6 z-[55] w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-2xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
      >
        {open ? <X size={24} /> : <HelpCircle size={26} />}
      </button>

      {open && (
        <div
          data-testid="help-panel"
          className="fixed bottom-24 left-4 right-4 sm:right-auto sm:left-6 sm:w-[400px] z-[55] rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden animate-fade-up"
          style={{ maxHeight: "min(70vh, 620px)" }}
        >
          {/* header */}
          <div className="px-4 py-3 border-b border-border bg-gradient-to-r from-primary/15 to-transparent">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                <Sparkles size={16} className="text-primary" />
              </div>
              <div className="min-w-0">
                <p className="font-display font-bold uppercase tracking-tight text-sm leading-none">Aide & Assistant</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">FAQ et réponses en direct</p>
              </div>
            </div>
          </div>

          {/* tabs */}
          <div className="flex border-b border-border shrink-0">
            <button
              data-testid="help-tab-faq"
              onClick={() => setTab("faq")}
              className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wide flex items-center justify-center gap-1.5 transition-colors ${tab === "faq" ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-foreground"}`}
            >
              <MessageCircleQuestion size={15} /> Questions
            </button>
            <button
              data-testid="help-tab-chat"
              onClick={() => setTab("chat")}
              className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wide flex items-center justify-center gap-1.5 transition-colors ${tab === "chat" ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-foreground"}`}
            >
              <Bot size={15} /> Assistant
            </button>
          </div>

          {/* FAQ tab */}
          {tab === "faq" && (
            <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="help-faq-list">
              {isAdmin && (
                <button data-testid="faq-add-btn" onClick={startNew}
                  className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-primary/40 text-primary text-xs font-semibold hover:bg-primary/10 transition-colors">
                  <Plus size={14} /> Ajouter une question
                </button>
              )}

              {editing && (
                <div className="rounded-xl border border-border bg-background p-3 space-y-2" data-testid="faq-edit-form">
                  <input data-testid="faq-question-input" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })}
                    placeholder="Question" className="w-full h-9 px-3 rounded-lg bg-secondary text-sm outline-none focus:ring-1 ring-primary" />
                  <textarea data-testid="faq-answer-input" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })}
                    placeholder="Réponse" rows={3} className="w-full px-3 py-2 rounded-lg bg-secondary text-sm outline-none focus:ring-1 ring-primary resize-none" />
                  <div className="flex gap-2">
                    <button data-testid="faq-save-btn" onClick={saveFaq} className="flex-1 h-8 rounded-lg bg-primary text-primary-foreground text-xs font-bold">Enregistrer</button>
                    <button onClick={() => { setEditing(null); setForm({ question: "", answer: "" }); }} className="px-3 h-8 rounded-lg bg-secondary text-xs font-semibold">Annuler</button>
                  </div>
                </div>
              )}

              {faqs.length === 0 && !editing && (
                <p className="text-center text-sm text-muted-foreground py-8">Aucune question pour le moment.</p>
              )}

              {faqs.map((f) => (
                <div key={f.id} data-testid="faq-item" className="rounded-xl border border-border overflow-hidden">
                  <button
                    onClick={() => setExpanded(expanded === f.id ? null : f.id)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-secondary/50 transition-colors"
                  >
                    <span className="flex-1 text-sm font-semibold">{f.question}</span>
                    <ChevronDown size={16} className={`text-muted-foreground transition-transform ${expanded === f.id ? "rotate-180" : ""}`} />
                  </button>
                  {expanded === f.id && (
                    <div className="px-3 pb-3">
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{f.answer}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <button data-testid="faq-ask-ai" onClick={() => askQuestion(f.question)}
                          className="text-[11px] font-semibold text-primary inline-flex items-center gap-1 hover:underline">
                          <Bot size={12} /> Demander à l'assistant
                        </button>
                        {isAdmin && (
                          <>
                            <button onClick={() => startEdit(f)} className="ml-auto text-muted-foreground hover:text-foreground p-1"><Pencil size={13} /></button>
                            <button data-testid={`faq-del-${f.id}`} onClick={() => removeFaq(f.id)} className="text-muted-foreground hover:text-destructive p-1"><Trash2 size={13} /></button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Chat tab */}
          {tab === "chat" && (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="help-chat-messages">
                {messages.length === 0 && (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-3">
                      <Bot size={24} className="text-primary" />
                    </div>
                    <p className="text-sm font-semibold">Posez votre question</p>
                    <p className="text-xs text-muted-foreground mt-1 px-4">Horaires, tarifs, inscription, utilisation de l'app… je vous réponds !</p>
                  </div>
                )}
                {messages.map((m, i) => <Bubble key={i} role={m.role} text={m.text} />)}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-secondary rounded-2xl rounded-bl-sm px-3.5 py-2.5">
                      <Loader2 size={16} className="animate-spin text-primary" />
                    </div>
                  </div>
                )}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-border flex items-end gap-2 shrink-0">
                <textarea
                  data-testid="help-chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Écrivez votre question…"
                  rows={1}
                  className="flex-1 max-h-24 px-3 py-2 rounded-xl bg-secondary text-sm outline-none focus:ring-1 ring-primary resize-none"
                />
                <button data-testid="help-chat-send" onClick={send} disabled={sending || !input.trim()}
                  className="w-9 h-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0 disabled:opacity-40">
                  <Send size={16} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
